import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { LiveSessionIconSvgComponent } from '../svg-sprites/general/live-session-icon-svg.component';
import { LessonsDBService } from '../services/lessons-db.service';
import { Router } from '@angular/router';
import { Subscription, combineLatest } from 'rxjs';

// add these imports
import { select, Store } from '@ngrx/store';
import { AppState } from '../store';
import { isAdmin, isCourseAdmin, isLoggedOut, selectUserCourses } from '../store/selectors';
import { UserPermissionsService } from '../shared/services/user-permissions.service';
import { PermissionKeys } from '../shared/services/permissions-key.enum';

@Component({
  selector: 'live-session-bar',
  imports: [LiveSessionIconSvgComponent],
  templateUrl: './live-session-bar.component.html',
  styleUrl: './live-session-bar.component.scss'
})
export class LiveSessionBarComponent implements OnInit, OnDestroy {
  private lessonsDB = inject(LessonsDBService);
  private router = inject(Router);

  // add these injections
  private store: Store<AppState> = inject(Store);
  private userPermissions: UserPermissionsService = inject(UserPermissionsService);

  isBannerVisible = false;
  currentLiveSession: any = null;
  private subscription?: Subscription;

  ngOnInit() {
    this.loadLiveSessions();
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  private isStudentRole(isAdminVal: boolean, isCourseAdminVal: boolean): boolean {
    return !isAdminVal && !isCourseAdminVal && !this.userPermissions.can(PermissionKeys.EditProduct);
  }

  loadLiveSessions() {
    const isLoggedOut$ = this.store.pipe(select(isLoggedOut));
    const isAdmin$ = this.store.pipe(select(isAdmin));
    const isCourseAdmin$ = this.store.pipe(select(isCourseAdmin));
    const userCourses$ = this.store.pipe(select(selectUserCourses));

    this.subscription = combineLatest([
      this.lessonsDB.loadAllVisibleLiveSessionsAndWebinars(),
      isLoggedOut$,
      isAdmin$,
      isCourseAdmin$,
      userCourses$
    ]).subscribe({
      next: ([sessions, isLoggedOutVal, isAdminVal, isCourseAdminVal, userCourses]) => {
        const now = new Date();

        // Must be logged in to show banner
        if (isLoggedOutVal) {
          this.isBannerVisible = false;
          this.currentLiveSession = null;
          return;
        }

        // Find sessions live right now
        const liveSessions = sessions
          .filter(session => {
            const s = session.liveSession || session.webinar;
            if (!s?.sessionDate) return false;
            const start = s.sessionDate.toDate();
            const durationHours = s.sessionDuration?.hours || 1;
            const durationMinutes = s.sessionDuration?.minutes || 0;
            const end = new Date(start);
            end.setHours(end.getHours() + durationHours);
            end.setMinutes(end.getMinutes() + durationMinutes);
            return now >= start && now <= end;
          })
          .sort((a, b) => {
            const aStart = (a.liveSession || a.webinar).sessionDate.toDate().getTime();
            const bStart = (b.liveSession || b.webinar).sessionDate.toDate().getTime();
            return aStart - bStart;
          });

        if (!liveSessions.length) {
          this.isBannerVisible = false;
          this.currentLiveSession = null;
          return;
        }

        // Role-based filtering
        const student = this.isStudentRole(isAdminVal, isCourseAdminVal);
        if (!student) {
          // Admin/course admin/instructor: show first live session
          this.currentLiveSession = liveSessions[0];
          this.isBannerVisible = true;
          return;
        }

        // Student: only show if there is a live session for an enrolled course
        const enrolledIds = new Set((userCourses || []).map(c => c.id));
        const studentLiveSessions = liveSessions.filter(s => enrolledIds.has(s.courseId));

        if (studentLiveSessions.length) {
          this.currentLiveSession = studentLiveSessions[0];
          this.isBannerVisible = true;
        } else {
          this.isBannerVisible = false;
          this.currentLiveSession = null;
        }
      }
    });
  }

  joinLiveSession() {
    if (this.currentLiveSession) {
      this.router.navigate([
        '/courses',
        this.currentLiveSession.courseUrl,
        this.currentLiveSession.sectionSeqNo || 1,
        'lessons',
        this.currentLiveSession.seqNo
      ]);
    }
  }

  closeLiveSolutionBanner() {
    this.isBannerVisible = false;
  }
}
